import React, { useState, useEffect } from 'react';
import { TipoMoneda, Cobro, CobroEstado, MetodoPago } from '../../types/cobros';
import { ModalRegistrarCobro } from './ModalRegistrarCobro';
import { ModalConfirmarCobroTotal } from './ModalConfirmarCobroTotal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import NumberInput from '@/components/ui/NumberInput';
import { Pencil, Trash2, Plus, DollarSign, Edit3, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface SeccionCobroCompactoProps {
  moneda: TipoMoneda;
  montoTotal: number;
  cobros: Cobro[];
  estado: CobroEstado;
  onRegistrarCobro: (data: { monto: number; fecha: string; descripcion: string; metodoPago?: MetodoPago | null; cajaId?: number | null }) => Promise<any>;
  onRegistrarCobroTotal: (cajaId?: number | null) => Promise<any>;
  onEditarCobro: (cobroId: string, data: { monto: number; fecha: string; descripcion: string }) => Promise<any>;
  onEliminarCobro: (cobroId: string) => Promise<any>;
  onEditarMontoTotal?: (nuevoMonto: number) => Promise<any>;
  isLoading?: boolean;
  canEdit?: boolean;
}

const formatearMonto = (monto: number, moneda: TipoMoneda): string => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 2,
  }).format(monto);
};

const formatearFecha = (fecha: string): string => {
  const [year, month, day] = fecha.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const SeccionCobroCompacto: React.FC<SeccionCobroCompactoProps> = ({
  moneda,
  montoTotal,
  cobros,
  estado,
  onRegistrarCobro,
  onRegistrarCobroTotal,
  onEditarCobro,
  onEliminarCobro,
  onEditarMontoTotal,
  isLoading = false,
  canEdit = true,
}) => {
  const [modalCobroOpen, setModalCobroOpen] = useState(false);
  const [modalCobroTotalOpen, setModalCobroTotalOpen] = useState(false);
  const [cobroEditando, setCobroEditando] = useState<Cobro | null>(null);
  const [cobroAEliminar, setCobroAEliminar] = useState<Cobro | null>(null);
  const [isEditingMonto, setIsEditingMonto] = useState(false);
  const [montoEditado, setMontoEditado] = useState<string>(String(montoTotal));
  const [errorMontoEdit, setErrorMontoEdit] = useState<string | null>(null);

  // Actualizar montoEditado cuando cambie montoTotal desde fuera
  useEffect(() => {
    if (!isEditingMonto) {
      setMontoEditado(String(montoTotal));
    }
  }, [montoTotal, isEditingMonto]);

  // Si no hay monto, no mostrar nada
  if (montoTotal <= 0) {
    return null;
  }

  const handleRegistrarCobro = async (data: { monto: number; fecha: string; descripcion: string; metodoPago?: MetodoPago | null; cajaId?: number | null }) => {
    if (cobroEditando) {
      await onEditarCobro(cobroEditando.id, data);
      setCobroEditando(null);
    } else {
      await onRegistrarCobro(data);
    }
    setModalCobroOpen(false);
  };

  const handleEditarCobro = (cobro: Cobro) => {
    setCobroEditando(cobro);
    setModalCobroOpen(true);
  };

  const handleConfirmarEliminar = async () => {
    if (cobroAEliminar) {
      await onEliminarCobro(cobroAEliminar.id);
      setCobroAEliminar(null);
    }
  };

  const handleIniciarEdicionMonto = () => {
    setMontoEditado(String(montoTotal));
    setIsEditingMonto(true);
    setErrorMontoEdit(null);
  };

  const handleCancelarEdicionMonto = () => {
    setMontoEditado(String(montoTotal));
    setIsEditingMonto(false);
    setErrorMontoEdit(null);
  };

  const handleGuardarMonto = async () => {
    if (!onEditarMontoTotal) return;

    const nuevoMonto = Number.parseFloat(montoEditado) || 0;

    // Validar que el monto no sea menor al total cobrado
    if (nuevoMonto < estado.totalCobrado) {
      setErrorMontoEdit(
        `El monto no puede ser menor a lo ya cobrado (${formatearMonto(estado.totalCobrado, moneda)})`
      );
      return;
    }

    // Validar que el monto sea mayor a 0
    if (nuevoMonto <= 0) {
      setErrorMontoEdit('El monto debe ser mayor a cero');
      return;
    }

    try {
      await onEditarMontoTotal(nuevoMonto);
      setIsEditingMonto(false);
      setErrorMontoEdit(null);
    } catch (error) {
      setErrorMontoEdit('Error al actualizar el monto');
    }
  };

  const getColorProgreso = () => {
    if (estado.porcentajeCobrado >= 100) return 'bg-emerald-500';
    if (estado.porcentajeCobrado >= 50) return 'bg-blue-500';
    return 'bg-amber-500';
  };

  const cobrosOrdenados = [...cobros].sort(
    (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
  );

  return (
    <>
      <div className="space-y-3">
        {/* Header con monto y progreso */}
        <div className={`rounded-xl border bg-gray-50`}>
          <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-4'>
            <div className="flex items-center gap-2 justify-end">
              <DollarSign className="h-4 w-4 text-gray-600" />
              <span className="text-lg font-semibold text-gray-700">{moneda}</span>
            </div>

            {/* TOTAL */}
            <div className="">
              <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                Total:
              </label>
              {isEditingMonto ? (
                <div className="flex items-center gap-1">
                  <NumberInput
                    value={montoEditado}
                    onChange={(value) => setMontoEditado(value)}
                    className="h-8 w-32 text-sm"
                    autoFocus
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleGuardarMonto}
                    disabled={isLoading}
                    className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleCancelarEdicionMonto}
                    disabled={isLoading}
                    className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatearMonto(montoTotal, moneda)}
                  </span>
                  {canEdit && onEditarMontoTotal && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handleIniciarEdicionMonto}
                      disabled={isLoading}
                      className="h-7 w-7"
                    >
                      <Edit3 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
              {errorMontoEdit && (
                <p className="text-xs text-red-600 mt-1">{errorMontoEdit}</p>
              )}
            </div>

            {/* COBRADO */}
            <div className="">
              <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                Cobrado:
              </label>
              <div className="text-sm text-gray-900">
                {formatearMonto(estado.totalCobrado, moneda)}
              </div>
            </div>

            {/* PENDIENTE */}
            <div className="">
              <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                Pendiente:
              </label>
              <div className={`text-sm font-semibold ${estado.saldoPendiente > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {formatearMonto(estado.saldoPendiente, moneda)}
              </div>
            </div>

            {/* % PROGRESO */}
            <div className="text-xl text-teal-600 font-bold">
              {Math.round(estado.porcentajeCobrado)}%
            </div>
          </div>
        </div>

        {/* Barra de progreso compacta */}
        <div className="space-y-1">
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${getColorProgreso()}`}
              style={{ width: `${Math.min(estado.porcentajeCobrado, 100)}%` }}
            />
          </div>
        </div>

        {/* Botones de acción */}
        {canEdit && (
          <div className="flex gap-2 overflow-hidden flex-wrap">
            <Button
              size="sm"
              onClick={() => setModalCobroOpen(true)}
              disabled={isLoading || estado.estaCompleto}
              className="flex-1 rounded-xl text-xs"
            >
              <Plus className="h-4 w-4 mr-1" />
              Registrar Cobro
            </Button>
            {estado.saldoPendiente > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setModalCobroTotalOpen(true)}
                disabled={isLoading || estado.estaCompleto}
                className="flex-1 rounded-xl text-xs"
              >
                <DollarSign className="h-4 w-4 mr-1" />
                Cobro Total
              </Button>
            )}
          </div>
        )}

        {/* Lista de cobros compacta */}
        {cobrosOrdenados.length > 0 && (
          <div className="space-y-1.5">
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Cobros registrados ({cobrosOrdenados.length})
            </h4>
            <div className="space-y-1">
              {cobrosOrdenados.map((cobro) => (
                <div
                  key={cobro.id}
                  className="flex items-center justify-between p-2 bg-gray-50  rounded-xl"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatearMonto(cobro.monto, moneda)}
                      </span>
                      {cobro.tipo === 'total' && (
                        <Badge variant="secondary" className="text-xs py-0">
                          Total
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span>{formatearFecha(cobro.fecha)}</span>
                      {cobro.descripcion && (
                        <>
                          <span>•</span>
                          <span className="truncate">{cobro.descripcion}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {canEdit && (
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => handleEditarCobro(cobro)}
                        disabled={isLoading}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setCobroAEliminar(cobro)}
                        disabled={isLoading}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {cobrosOrdenados.length === 0 && (
          <div className="text-center py-4 text-sm text-gray-500">
            No hay cobros registrados
          </div>
        )}
      </div>

      {/* Modales */}
      <ModalRegistrarCobro
        isOpen={modalCobroOpen}
        onClose={() => {
          setModalCobroOpen(false);
          setCobroEditando(null);
        }}
        onConfirm={handleRegistrarCobro}
        moneda={moneda}
        saldoPendiente={estado.saldoPendiente}
        isLoading={isLoading}
        cobroEditar={cobroEditando}
        montoTotal={montoTotal}
        totalCobradoActual={estado.totalCobrado}
      />

      <ModalConfirmarCobroTotal
        isOpen={modalCobroTotalOpen}
        onClose={() => setModalCobroTotalOpen(false)}
        onConfirm={async (cajaId) => {
          await onRegistrarCobroTotal(cajaId);
          setModalCobroTotalOpen(false);
        }}
        moneda={moneda}
        saldoPendiente={estado.saldoPendiente}
        isLoading={isLoading}
      />

      {/* Modal confirmación de eliminación */}
      <Dialog open={!!cobroAEliminar} onOpenChange={() => setCobroAEliminar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              ¿Está seguro de eliminar este cobro de{' '}
              <strong className="text-foreground">
                {cobroAEliminar && formatearMonto(cobroAEliminar.monto, moneda)}
              </strong>?
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCobroAEliminar(null)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmarEliminar}
              loading={isLoading}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
